/**
 * useEditorPreferences Hook
 * Verwaltet benutzerspezifische Editor-Einstellungen (global).
 * Lädt vom Backend und speichert Änderungen mit Debounce.
 * 
 * Einstellungen:
 * - deckEditorColumnCount: Anzahl der Spalten (3-12)
 * - deckEditorViewMode: 'stacks' | 'rows' | 'table' | 'compact'
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { json } from '../api/apiClient';

// ========== KONFIGURATIONSKONSTANTEN ==========
// Debounce-Zeit für Speichern der Einstellungen (ms)
export const PREFERENCES_DEBOUNCE_MS = 2000;

// Spalten-Grenzen
export const MIN_COLUMNS = 3;
export const MAX_COLUMNS = 12;
export const DEFAULT_COLUMN_COUNT = 6;

// View-Modi
export const VIEW_MODES = {
  STACKS: 'stacks',
  ROWS: 'rows',
  TABLE: 'table',
  COMPACT: 'compact',
};

// Seiten der Kommandobrücke
export const BRIDGE_SIDES = ['left', 'right'];
export const DEFAULT_BRIDGE_SIDE = 'left';

// Katalog-Seitenspalte: Breiten-Grenzen und Reiter.
// Das Minimum hängt an der Kachelbreite der Raster-Variante `browse` (CardGrid.css): eine
// Kachel misst Kartenbild-Minimum + Kachel-Zugabe = 278px, die Seitenspalte legt 27px Polster
// und Ziehgriff darum. Wer eines von beidem ändert, rechnet hier nach — sonst schiebt die
// schmalste Spalte eine waagerechte Scrollleiste unter die Kacheln. Dieselbe Untergrenze
// prüft der Server (userPreferenceController.ts, userPreferenceService.ts).
export const MIN_CATALOG_WIDTH = 305;
export const MAX_CATALOG_WIDTH = 900;
export const DEFAULT_CATALOG_WIDTH = 380;
export const CATALOG_TABS = ['suggestions', 'search', 'admin'];
export const DEFAULT_CATALOG_TAB = 'suggestions';

// Spalten-Grenzen der mobilen Editor-Ansicht
export const MOBILE_MIN_COLUMNS = 1;
export const MOBILE_MAX_COLUMNS = 4;
export const DEFAULT_MOBILE_COLUMN_COUNT = 3;

/**
 * Hook für Editor-Einstellungen
 */
export function useEditorPreferences(userId) {
  const [columnCount, setColumnCountState] = useState(DEFAULT_COLUMN_COUNT);
  const [viewMode, setViewModeState] = useState(VIEW_MODES.STACKS);
  const [bridgeSide, setBridgeSideState] = useState(DEFAULT_BRIDGE_SIDE);
  const [catalogWidth, setCatalogWidthState] = useState(DEFAULT_CATALOG_WIDTH);
  const [catalogTab, setCatalogTabState] = useState(DEFAULT_CATALOG_TAB);
  const [mobileColumnCount, setMobileColumnCountState] = useState(DEFAULT_MOBILE_COLUMN_COUNT);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Refs für Debounce
  const saveTimeoutRef = useRef(null);
  const pendingUpdatesRef = useRef({});

  // Merkt sich pro Feld, ob der Nutzer es schon selbst gesetzt hat, bevor die
  // initiale GET-Antwort eintrifft. Der Request unten läuft ohne Abbruch weiter,
  // bis er antwortet — bei einem langsamen Server-Start oder einem schnellen
  // Klick direkt nach dem Öffnen des Editors kann diese Antwort erst nach einer
  // lokalen Änderung zurückkommen und würde sie sonst mit dem alten Serverstand
  // überschreiben.
  const localOverrideRef = useRef({ columnCount: false, viewMode: false, bridgeSide: false, catalogWidth: false, catalogTab: false, mobileColumnCount: false });

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Lade Einstellungen vom Backend bei User-Wechsel
  useEffect(() => {
    localOverrideRef.current = { columnCount: false, viewMode: false, bridgeSide: false, catalogWidth: false, catalogTab: false, mobileColumnCount: false };

    if (!userId) {
      setIsLoaded(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        const data = await json('/api/me/preferences');
        const prefs = data?.preferences || data;

        if (prefs) {
          if (typeof prefs.deckEditorColumnCount === 'number' && !localOverrideRef.current.columnCount) {
            setColumnCountState(prefs.deckEditorColumnCount);
          }
          if (prefs.deckEditorViewMode && !localOverrideRef.current.viewMode) {
            setViewModeState(prefs.deckEditorViewMode);
          }
          if (prefs.deckEditorBridgeSide && !localOverrideRef.current.bridgeSide) {
            setBridgeSideState(prefs.deckEditorBridgeSide);
          }
          if (typeof prefs.deckEditorCatalogWidth === 'number' && !localOverrideRef.current.catalogWidth) {
            // Gespeicherte Breiten können unter der heutigen Untergrenze liegen; ungeklemmt
            // zeigte die Spalte eine waagerechte Scrollleiste unter den Kacheln.
            setCatalogWidthState(Math.max(MIN_CATALOG_WIDTH, Math.min(MAX_CATALOG_WIDTH, prefs.deckEditorCatalogWidth)));
          }
          if (prefs.deckEditorCatalogTab && !localOverrideRef.current.catalogTab) {
            setCatalogTabState(prefs.deckEditorCatalogTab);
          }
          if (typeof prefs.mobileColumnCount === 'number' && !localOverrideRef.current.mobileColumnCount) {
            setMobileColumnCountState(prefs.mobileColumnCount);
          }
        }

        setIsLoaded(true);
        console.log('[EditorPreferences] Einstellungen geladen:', prefs);
      } catch (err) {
        console.error('[EditorPreferences] Fehler beim Laden der Einstellungen:', err);
        setIsLoaded(true); // Trotzdem als geladen markieren (mit Defaults)
      }
    };

    loadPreferences();
  }, [userId]);
  
  /**
   * Speichert Einstellungen im Backend (debounced)
   */
  const savePreferencesToBackend = useCallback(async () => {
    const updates = { ...pendingUpdatesRef.current };
    pendingUpdatesRef.current = {};
    
    if (Object.keys(updates).length === 0) return;
    
    try {
      await json('/api/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      console.log('[EditorPreferences] Einstellungen gespeichert:', updates);
    } catch (err) {
      console.error('[EditorPreferences] Fehler beim Speichern der Einstellungen:', err);
    }
  }, []);
  
  /**
   * Plant das Speichern mit Debounce
   */
  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(savePreferencesToBackend, PREFERENCES_DEBOUNCE_MS);
  }, [savePreferencesToBackend]);
  
  /**
   * Setzt die Spaltenanzahl
   */
  const setColumnCount = useCallback((count) => {
    const validCount = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, count));
    localOverrideRef.current.columnCount = true;
    setColumnCountState(validCount);

    pendingUpdatesRef.current.deckEditorColumnCount = validCount;
    scheduleSave();
  }, [scheduleSave]);
  
  /**
   * Setzt den View-Modus
   */
  const setViewMode = useCallback((mode) => {
    if (!Object.values(VIEW_MODES).includes(mode)) {
      console.warn('[EditorPreferences] Ungültiger View-Modus:', mode);
      return;
    }
    
    localOverrideRef.current.viewMode = true;
    setViewModeState(mode);

    pendingUpdatesRef.current.deckEditorViewMode = mode;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Setzt die Seite der Kommandobrücke
   */
  const setBridgeSide = useCallback((side) => {
    if (!BRIDGE_SIDES.includes(side)) {
      console.warn('[EditorPreferences] Ungültige Brückenseite:', side);
      return;
    }

    localOverrideRef.current.bridgeSide = true;
    setBridgeSideState(side);
    pendingUpdatesRef.current.deckEditorBridgeSide = side;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Setzt die Breite der Katalog-Seitenspalte (in Pixel, MIN_CATALOG_WIDTH bis MAX_CATALOG_WIDTH)
   */
  const setCatalogWidth = useCallback((width) => {
    const clamped = Math.round(Math.max(MIN_CATALOG_WIDTH, Math.min(MAX_CATALOG_WIDTH, width)));
    if (!Number.isFinite(clamped)) return;

    localOverrideRef.current.catalogWidth = true;
    setCatalogWidthState(clamped);
    pendingUpdatesRef.current.deckEditorCatalogWidth = clamped;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Setzt den zuletzt gewählten Reiter des Katalogs
   */
  const setCatalogTab = useCallback((tab) => {
    if (!CATALOG_TABS.includes(tab)) {
      console.warn('[EditorPreferences] Ungültiger Katalog-Reiter:', tab);
      return;
    }

    localOverrideRef.current.catalogTab = true;
    setCatalogTabState(tab);
    pendingUpdatesRef.current.deckEditorCatalogTab = tab;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Setzt die Spaltenanzahl der mobilen Editor-Ansicht
   */
  const setMobileColumnCount = useCallback((count) => {
    const clamped = Math.max(MOBILE_MIN_COLUMNS, Math.min(MOBILE_MAX_COLUMNS, count));
    setMobileColumnCountState(clamped);
    localOverrideRef.current.mobileColumnCount = true;
    pendingUpdatesRef.current.mobileColumnCount = clamped;
    scheduleSave();
  }, [scheduleSave]);

  /**
   * Gibt die Spalten-Optionen für Select zurück
   */
  const getColumnOptions = useCallback(() => {
    return Array.from(
      { length: MAX_COLUMNS - MIN_COLUMNS + 1 },
      (_, i) => MIN_COLUMNS + i
    );
  }, []);
  
  return {
    columnCount,
    viewMode,
    bridgeSide,
    catalogWidth,
    catalogTab,
    mobileColumnCount,
    isLoaded,
    setColumnCount,
    setViewMode,
    setBridgeSide,
    setCatalogWidth,
    setCatalogTab,
    setMobileColumnCount,
    getColumnOptions,
    VIEW_MODES,
    MIN_COLUMNS,
    MAX_COLUMNS,
  };
}

export default useEditorPreferences;
